import React, { useMemo, useState } from "react";
import {
    Search,
    ChevronDown,
    ShoppingCart,
    PackageCheck,
    Truck,
    XCircle,
    IndianRupee,
    MapPin,
    Calendar,
} from "lucide-react";

const ORDERS = [
    {
        id: "ORD-3051",
        customer: "Ravi Teja",
        item: "EV Battery Enclosure — Prototype Run",
        centre: "Vizag",
        amount: 48500,
        payment: "Paid",
        status: "Delivered",
        date: "15 Jul 2026",
    },
    {
        id: "ORD-3049",
        customer: "Lakshmi Devi",
        item: "Agri Drone Chassis — CNC Machining",
        centre: "Vijayawada",
        amount: 32750,
        payment: "Paid",
        status: "In Production",
        date: "16 Jul 2026",
    },
    {
        id: "ORD-3046",
        customer: "Sandeep Reddy",
        item: "IoT Smart Water Sensor — PCB Fab",
        centre: "Tirupati",
        amount: 12800,
        payment: "Pending",
        status: "Awaiting Payment",
        date: "17 Jul 2026",
    },
    {
        id: "ORD-3042",
        customer: "Meera Iyer",
        item: "Medical Infusion Housing — 3D Print",
        centre: "Kurnool",
        amount: 21400,
        payment: "Paid",
        status: "Shipped",
        date: "14 Jul 2026",
    },
    {
        id: "ORD-3038",
        customer: "Kiran Babu",
        item: "EV Battery Enclosure — Sheet Metal",
        centre: "Guntur",
        amount: 56200,
        payment: "Refunded",
        status: "Cancelled",
        date: "12 Jul 2026",
    },
    {
        id: "ORD-3035",
        customer: "Divya Sri",
        item: "Agri Drone Chassis — Composite Layup",
        centre: "Nellore",
        amount: 39900,
        payment: "Paid",
        status: "Delivered",
        date: "10 Jul 2026",
    },
];

const STATUS_STYLES = {
    "Awaiting Payment": "bg-amber-100 text-amber-700",
    "In Production": "bg-blue-100 text-blue-700",
    Shipped: "bg-indigo-100 text-indigo-700",
    Delivered: "bg-green-100 text-green-700",
    Cancelled: "bg-red-100 text-red-700",
};

const PAYMENT_STYLES = {
    Paid: "bg-green-50 text-green-600 border border-green-200",
    Pending: "bg-amber-50 text-amber-600 border border-amber-200",
    Refunded: "bg-slate-50 text-slate-600 border border-slate-200",
};

function StatCard({ icon: Icon, label, value, tone }) {
    return (
        <div className="flex items-center gap-4 rounded-2xl border border-gray-200 bg-white p-5">
            <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tone}`}>
                <Icon className="h-5 w-5" />
            </span>
            <div>
                <p className="text-2xl font-bold text-gray-900">{value}</p>
                <p className="text-sm text-gray-500">{label}</p>
            </div>
        </div>
    );
}

export default function Ordermanagment() {
    const [query, setQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("All Status");
    const [paymentFilter, setPaymentFilter] = useState("All Payments");

    const statuses = useMemo(
        () => ["All Status", ...new Set(ORDERS.map((o) => o.status))],
        []
    );
    const payments = useMemo(
        () => ["All Payments", ...new Set(ORDERS.map((o) => o.payment))],
        []
    );

    const counts = useMemo(() => {
        const totalRevenue = ORDERS.filter((o) => o.payment === "Paid").reduce(
            (sum, o) => sum + o.amount,
            0
        );
        const inProduction = ORDERS.filter((o) => o.status === "In Production").length;
        const delivered = ORDERS.filter((o) => o.status === "Delivered").length;
        const cancelled = ORDERS.filter((o) => o.status === "Cancelled").length;
        return { total: ORDERS.length, totalRevenue, inProduction, delivered, cancelled };
    }, []);

    const filtered = useMemo(() => {
        return ORDERS.filter((o) => {
            const matchesQuery =
                query.trim() === "" ||
                [o.id, o.customer, o.item, o.centre].some((field) =>
                    field.toLowerCase().includes(query.trim().toLowerCase())
                );
            const matchesStatus = statusFilter === "All Status" || o.status === statusFilter;
            const matchesPayment = paymentFilter === "All Payments" || o.payment === paymentFilter;
            return matchesQuery && matchesStatus && matchesPayment;
        });
    }, [query, statusFilter, paymentFilter]);

    return (
        <div className="h-screen w-full overflow-y-auto bg-gray-50 scrollbar-hide">
            <style>{`
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                }
                .scrollbar-hide {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>

            <div className="p-6">
                {/* Page heading */}
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">Order management</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Track fabrication and prototyping orders across all centres
                    </p>
                </div>

                {/* Stat cards */}
                <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <StatCard icon={ShoppingCart} label="Total orders" value={counts.total} tone="bg-indigo-50 text-indigo-500" />
                    <StatCard
                        icon={IndianRupee}
                        label="Revenue collected"
                        value={`₹${counts.totalRevenue.toLocaleString("en-IN")}`}
                        tone="bg-green-50 text-green-500"
                    />
                    <StatCard icon={Truck} label="In production" value={counts.inProduction} tone="bg-blue-50 text-blue-500" />
                    <StatCard icon={XCircle} label="Cancelled" value={counts.cancelled} tone="bg-red-50 text-red-500" />
                </div>

                {/* Table card */}
                <div className="w-full rounded-2xl border border-gray-200 bg-white shadow-sm">
                    {/* Header */}
                    <div className="flex flex-col gap-4 border-b border-gray-100 p-6 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">Recent orders</h2>
                            <p className="mt-1 text-sm text-gray-500">
                                {filtered.length} of {ORDERS.length} orders
                            </p>
                        </div>

                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                            <div className="relative">
                                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Search order, customer, item..."
                                    className="w-64 rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm text-gray-700 placeholder-gray-400 outline-none focus:border-gray-300 focus:bg-white"
                                />
                            </div>

                            <div className="relative">
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="appearance-none rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 outline-none focus:border-gray-300"
                                >
                                    {statuses.map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            </div>

                            <div className="relative">
                                <select
                                    value={paymentFilter}
                                    onChange={(e) => setPaymentFilter(e.target.value)}
                                    className="appearance-none rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 outline-none focus:border-gray-300"
                                >
                                    {payments.map((p) => (
                                        <option key={p} value={p}>
                                            {p}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[1100px] table-fixed border-collapse text-left">
                            <thead>
                                <tr className="bg-gray-50 text-xs uppercase tracking-wide text-gray-400">
                                    <th className="w-[28%] px-6 py-3 font-medium">Order</th>
                                    <th className="w-[14%] px-6 py-3 font-medium">Customer</th>
                                    <th className="w-[10%] px-6 py-3 font-medium">Centre</th>
                                    <th className="w-[12%] px-6 py-3 font-medium">Amount</th>
                                    <th className="w-[11%] px-6 py-3 font-medium">Payment</th>
                                    <th className="w-[13%] px-6 py-3 font-medium">Status</th>
                                    <th className="w-[12%] px-6 py-3 font-medium">Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((o, idx) => (
                                    <tr
                                        key={o.id}
                                        className={`text-sm ${idx !== filtered.length - 1 ? "border-b border-gray-100" : ""}`}
                                    >
                                        <td className="px-6 py-4">
                                            <p className="font-semibold text-gray-900">{o.item}</p>
                                            <p className="mt-0.5 text-xs text-gray-400">{o.id}</p>
                                        </td>
                                        <td className="px-6 py-4 text-gray-600">{o.customer}</td>
                                        <td className="px-6 py-4 text-gray-600">
                                            <div className="flex items-center gap-1.5">
                                                <MapPin className="h-4 w-4 shrink-0 text-gray-400" />
                                                {o.centre}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 font-medium text-gray-900">
                                            ₹{o.amount.toLocaleString("en-IN")}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${PAYMENT_STYLES[o.payment]}`}>
                                                {o.payment}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${STATUS_STYLES[o.status]}`}>
                                                {o.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-gray-500">
                                            <div className="flex items-center gap-1.5">
                                                <Calendar className="h-4 w-4 shrink-0 text-gray-400" />
                                                {o.date}
                                            </div>
                                        </td>
                                    </tr>
                                ))}

                                {filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-400">
                                            No orders match your filters.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}